import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import { KeyboardDatePickerProps } from '@material-ui/pickers';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { DateTimePicker } from '@utmost.co/utmost-design-system';
import { formatDate } from '../../forms/utils/dateHelpers';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { ReadOnly } from '../ReadOnly/ReadOnly';
import { getAnonymousName } from '../utils/disableBrowserAutocomplete';
import { Label } from '../Label/Label';
import { PermissionHelper } from '../common/PermissionHelper';

const createDateString = (value: ParsableDate, format: string) => {
  try {
    return formatDate(value, format || 'dd MMM yyyy HH:mm', `yyyy-MM-dd'T'HH:mm:ss.SSS`);
  } catch {
    return null;
  }
};

export const DateTimePickerAtom: React.FC<React.PropsWithChildren<DatePicker4Props>> = (props: any) => {
  const { getDisabled, readonly, getFieldPermissions, permissionDebugger } = useSmartFormContext();
  const permissions = getFieldPermissions(props.name);

  const {
    onChange,
    onBlur,
    onError,
    views,
    openTo,
    minDate,
    minDateMessage,
    maxDate,
    maxDateMessage,
    helperText,
    mapInputValue,
    invalidDateMessage,
    name,
    value,
    error,
    fullWidth = true,
    ...restProps
  }: DatePicker4Props & {
    mapInputValue: any;
    error: any;
    value: string;
  } = props;

  if (readonly || props.readOnly) {
    const dateString = createDateString(value, props.format);
    return <ReadOnly permission={permissions} permissionsId={props.id} label={props.label} value={dateString || '-'} />;
  }

  const anonName = getAnonymousName(props.name);

  let { disabled } = props;

  if (getDisabled(props.name)) {
    disabled = true;
  }

  const formHelperTextProps = {
    'data-test': `${name}-field-helper`,
    id: `${name}-helper-text`
  };

  return (
    <FormControl fullWidth={fullWidth} disabled>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <Label
        text={props.label}
        dataTest={name!}
        error={props.error}
        required={props.required}
        htmlFor={name}
        disabled={props.disabled}
      />
      <DateTimePicker
        error={error}
        helperText={helperText}
        minDate={minDate}
        maxDate={maxDate}
        InputLabelProps={{
          shrink: true
        }}
        FormHelperTextProps={formHelperTextProps}
        onBlur={onBlur}
        onChange={onChange as (date: string | null, value?: string | null) => void}
        onError={props.onError ? props.onError : () => {}}
        {...restProps}
        disabled={disabled}
        value={value}
        fullWidth={fullWidth}
        name={anonName}
        id={name}
      />
      {permissionDebugger && <PermissionHelper permission={permissions} />}
    </FormControl>
  );
};

export interface DatePicker4Props extends KeyboardDatePickerProps {}
